import * as XLSX from "xlsx";

export const IMPORT_TEMPLATE_VERSION = "1";
export const IMPORT_SHEET_NAME = "활동자료";
export const IMPORT_HEADERS = ["기간", "사업장", "설비", "활동자료", "값", "단위", "출처", "비고"] as const;

export type ImportRecord = {
  period: string;
  site: string;
  equipment: string;
  activity: string;
  amount: number | null;
  unit: string;
  source: string;
  note: string;
};

export type ParsedImportFile = {
  headers: string[];
  rows: string[][];
  records: ImportRecord[];
};

const GUIDE_ROWS = [
  ["항목", "필수 여부", "작성 방법", "예시"],
  ["기간", "선택", "YYYY-MM 또는 YYYY 형식으로 입력합니다.", "2026-09"],
  ["사업장", "선택", "여러 사업장을 한 파일로 올릴 때 입력합니다.", "울산 생산시설"],
  ["설비", "선택", "활동자료가 발생한 설비 이름을 입력합니다.", "수전해 스택"],
  ["활동자료", "필수", "전력, 천연가스, 용수처럼 사용량을 기록합니다.", "전력 사용량"],
  ["값", "필수", "숫자만 입력합니다. 천 단위 쉼표는 사용하지 않아도 됩니다.", "1250.5"],
  ["단위", "필수", "값의 단위를 입력합니다.", "kWh"],
  ["출처", "선택", "청구서, 계측기, 담당자 입력 등 근거를 입력합니다.", "전력 청구서"],
  ["비고", "선택", "검토에 필요한 내용을 입력합니다.", "9월 사용량"],
];

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function downloadCsvTemplate() {
  const csv = `\uFEFF${IMPORT_HEADERS.join(",")}\n`;
  downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), "h2lca-활동자료-양식.csv");
}

export function downloadExcelTemplate() {
  const workbook = XLSX.utils.book_new();
  const activitySheet = XLSX.utils.aoa_to_sheet([[...IMPORT_HEADERS]]);
  const guideSheet = XLSX.utils.aoa_to_sheet([
    ["수소 LCA 활동자료 업로드 양식"],
    ["양식 버전", IMPORT_TEMPLATE_VERSION],
    [],
    ...GUIDE_ROWS,
  ]);
  XLSX.utils.book_append_sheet(workbook, activitySheet, IMPORT_SHEET_NAME);
  XLSX.utils.book_append_sheet(workbook, guideSheet, "작성 안내");
  const content = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  downloadBlob(new Blob([content], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "h2lca-활동자료-양식.xlsx");
}

function parseCsv(text: string): string[][] {
  const source = text.replace(/^\uFEFF/, "");
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < source.length; i += 1) {
    const char = source[i];
    const next = source[i + 1];
    if (char === '"' && quoted && next === '"') {
      cell += '"';
      i += 1;
    } else if (char === '"') {
      quoted = !quoted;
    } else if (char === "," && !quoted) {
      row.push(cell.trim());
      cell = "";
    } else if ((char === "\n" || char === "\r") && !quoted) {
      if (char === "\r" && next === "\n") i += 1;
      row.push(cell.trim());
      cell = "";
      if (row.some((value) => value !== "")) rows.push(row);
      row = [];
    } else {
      cell += char;
    }
  }
  row.push(cell.trim());
  if (row.some((value) => value !== "")) rows.push(row);
  return rows;
}

function numberValue(value: string): number | null {
  const clean = value.replaceAll(",", "").trim();
  if (!clean) return null;
  const number = Number(clean);
  return Number.isFinite(number) ? number : null;
}

function validateRows(rows: string[][]): ParsedImportFile {
  const headers = rows.shift() ?? [];
  const expected = [...IMPORT_HEADERS];
  if (headers.length !== expected.length || headers.some((header, index) => header !== expected[index])) {
    throw new Error(`플랫폼 양식과 열 구성이 다릅니다. ${expected.join(", ")} 순서를 그대로 사용해 주세요.`);
  }
  const dataRows = rows.filter((values) => values.some((value) => value !== ""));
  if (dataRows.length === 0) throw new Error("활동자료 시트에 데이터 행이 없습니다.");
  if (dataRows.length > 5000) throw new Error("한 번에 최대 5,000행까지 업로드할 수 있습니다.");
  return {
    headers,
    rows: dataRows,
    records: dataRows.map((values) => ({
      period: values[0] ?? "",
      site: values[1] ?? "",
      equipment: values[2] ?? "",
      activity: values[3] ?? "",
      amount: numberValue(values[4] ?? ""),
      unit: values[5] ?? "",
      source: values[6] ?? "",
      note: values[7] ?? "",
    })),
  };
}

export async function parseImportFile(file: File): Promise<ParsedImportFile> {
  const lowerName = file.name.toLowerCase();
  if (lowerName.endsWith(".csv")) return validateRows(parseCsv(await file.text()));
  if (!lowerName.endsWith(".xlsx")) throw new Error("CSV 또는 XLSX 양식 파일만 업로드할 수 있습니다.");

  const workbook = XLSX.read(await file.arrayBuffer(), { type: "array", raw: false });
  const sheet = workbook.Sheets[IMPORT_SHEET_NAME];
  if (!sheet) throw new Error(`엑셀 파일에 ${IMPORT_SHEET_NAME} 시트가 없습니다.`);
  const rows = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1, defval: "", raw: false });
  return validateRows(rows);
}
